/* RUG SIZE GRID — the rung cards' behaviour.
 *
 * THE CARDS ARE NOT THE FORM CONTROL. `<select name="options[Size]">` is, and
 * it stays in the DOM inside the All-sizes disclosure. A card writes that
 * select and dispatches `change`, so Horizon's own variant-picker does the
 * variant switch, the price update and the gallery swap exactly as before —
 * and `rug-custom-size.js`, which finds a typed size among what we already
 * sell with `select[name="options[Size]"]`, keeps working untouched.
 *
 * WITH THIS FILE ABSENT THE PAGE STILL SELLS EVERY SIZE. The disclosure and
 * the select are server-rendered; only the cards stop responding. That is the
 * same no-JavaScript posture `rug-variant-picker.liquid` takes for its anchor.
 *
 * EVERY LISTENER IS DELEGATED FROM `document`. `variant-picker.js` MORPHS the
 * whole <variant-picker> element on each change, so anything bound to a card
 * is discarded on the first click — the identical failure the shape-entry
 * script documents. Delegation survives the morph, and `aria-checked` comes
 * back correct from Liquid because the server re-renders it.
 */
(function () {
  'use strict';

  var GRID = '[data-rz-size-grid]';
  var CARD = '.rz-sg__card';

  function gridOf(el) { return el.closest(GRID); }

  function selectFor(grid) {
    /* The grid names its select rather than guessing at one: a page may carry
     * a second picker (quick-add, a recommendation card) and `querySelector`
     * on the document would find whichever came first. */
    var sel = grid.getAttribute('data-select');
    var node = sel ? document.querySelector(sel) : null;
    if (node) return node;
    var scope = grid.closest('variant-picker') || document;
    return scope.querySelector('select[name^="options["]');
  }

  function optionFor(select, cm) {
    if (!select) return null;
    for (var i = 0; i < select.options.length; i++) {
      if (select.options[i].value.indexOf(cm) > -1) return select.options[i];
    }
    return null;
  }

  /* ONLY THE SHAPE ON SCREEN. Since 2026-09-19 the grid carries EVERY shape's
   * cards and hides all but one, so a press swaps a class instead of fetching
   * the page — measured at 4,975 ms before, which is the whole reason. Every
   * reader here asks about the shape the customer is looking at, so the hidden
   * groups are filtered out at the one place they are all read from. Leaving
   * them in would give the keyboard four shapes to arrow through and let the
   * off-ladder reconciler land on a card nobody can see. */
  function cards(grid) {
    return Array.prototype.slice.call(grid.querySelectorAll(CARD))
      .filter(function (c) {
        var g = c.closest('[data-rz-family]');
        return !g || !g.hidden;
      });
  }

  /* RUG-FORK (storefront v2, round 5): THE PRICE MOVES IN THE SAME FRAME AS THE
   * CLICK. Ported from the repo copy (2026-09-22): `rug-instant-price.js` owns the
   * figure and its formatting (`blocks/price.liquid`'s `script.rz-variant-prices`);
   * this file only says which variant is now selected. Absent script or unknown
   * variant is a no-op and the price arrives with the fetch as before. */
  function instantPrice(id, from) {
    var ip = window.RugInstantPrice;
    if (ip && id) { try { ip.apply(id, from); } catch (e) { /* never block the selection */ } }
  }

  /* RUG-FORK (storefront v2): ported from the repo copy of 2026-09-24. A
   * sold-out card is drawn (struck, "Out of stock") and is never selected. */
  function buyable(card) {
    return !card || card.getAttribute('data-available') !== '0';
  }

  function choose(card) {
    if (!buyable(card)) return;
    var grid = gridOf(card);
    if (!grid) return;
    var cm = '(' + card.getAttribute('data-w-cm') + 'x'
           + card.getAttribute('data-l-cm') + ' cm)';
    var select = selectFor(grid);
    var opt = optionFor(select, cm);

    if (opt) {
      /* FAST PATH — the size exists under the CURRENTLY selected shape, so
       * Horizon can switch in place with no navigation. */
      instantPrice(opt.getAttribute('data-variant-id')
                   || card.getAttribute('data-variant-id'), card);
      select.value = opt.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      mark(grid, card);
      syncUrl(select);
      return;
    }

    /* THERE IS NO CROSS-SHAPE BRANCH ANY MORE, and deleting it is the point.
     *
     * The grid renders only the SELECTED shape's rungs and Liquid populates
     * `.variant` for exactly those, so every card has a variant id and the
     * fast path above always takes it. What stood here routed a card of
     * another shape through the shape-entry metafield, and it was the source
     * of both faults this component shipped: a select carrying another
     * shape's sizes took the fast path onto a value with no variant and left
     * the product with no price, and the fallback moved a customer who wanted
     * an 80x100 RECTANGLE onto an 80x80 SQUARE.
     *
     * A card with no variant id is a template fault, not a shape to navigate
     * to, so it does nothing rather than guessing.
     *
     * The shape BUTTONS still cross shapes, through the picker's own
     * shape-entry listener in `rug-variant-picker.liquid`. That is unchanged.
     */
  }


  /* ARRIVED ON A SIZE WE NO LONGER OFFER: SAY SO, AND STAY IN THE SHAPE.
   *
   * Shopify resolves `?variant=<closed>` to the first BUYABLE variant and
   * says nothing, so the page is consistent and the customer is looking at a
   * different rug from the one they followed. The URL still carries the id
   * they asked for and the page has settled on another; that difference is
   * the whole signal, and it exists only in the browser (Liquid's
   * `selected_value` and `selected_variant` both read as the substitute —
   * measured).
   *
   * THE LINK IS NOT REDIRECTED AND NOTHING IS HIDDEN. The notice names the
   * size where we can resolve it, points at the grid, and points at the
   * custom entry, which is the real answer for a size outside the ladder.
   */
  /* THE SIGNAL IS THAT NO CARD IS CHECKED.
   *
   * Three readings were tried before this one, and the first two were wrong
   * about where the fact lives. Liquid's `selected_value` and
   * `selected_variant` both resolve a closed variant to the first buyable one,
   * so neither ever names what was asked for. Comparing `?variant=` with the
   * form's id fails for the opposite reason: Shopify DOES honour the parameter
   * for an unavailable variant, so the two agree and the mismatch is invisible.
   *
   * What is always true is that the grid renders a card for every size we
   * offer and checks the one that is selected. If the page has settled on a
   * size and NOTHING is checked, that size is not one we offer. No URL
   * parsing, no fetch, no second source — the grid already knows.
   */
  function currentVariant() {
    var n = document.querySelector('variant-picker script[type="application/json"]');
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { return null; }
  }

  /* NO NOTICE. The grey box that named the closed size was removed by direct
   * decision (2026-09-19); what it explained still happens silently, which is
   * the point — an old `?variant=` link resolves, and the selection moves to
   * the nearest rung OF THE SAME SHAPE so somebody who wanted an 80x100
   * rectangle does not land on an 80x80 square. The move is the behaviour; the
   * sentence was the decoration. */

  function nearestInFamily(fam, wantArea, buyable) {
    var grid = document.querySelector(GRID);
    if (!grid) return null;
    var best = null;
    cards(grid).forEach(function (c) {
      if (c.getAttribute('data-family') !== fam) return;
      /* RUG-FORK (storefront v2): when stock is known, only a size that can be
         bought may be chosen — the nearest rung to an off-ladder 80x100 was a
         sold-out 120x180, and the woven page opened on "Sold out". */
      if (buyable && !buyable[c.getAttribute('data-variant-id')]) return;
      if (c.getAttribute('data-available') === '0') return;
      var a = Number(c.getAttribute('data-w-cm')) * Number(c.getAttribute('data-l-cm'));
      var d = Math.abs(a - wantArea);
      if (!best || d < best[0]) best = [d, c];
    });
    return best && best[1];
  }

  function reconcileOffLadder() {
    var grid = document.querySelector(GRID);
    if (!grid) return;

    /* TWO SIGNALS, EITHER ONE FIRES, BECAUSE SHOPIFY DOES BOTH.
     * Measured on the same URL minutes apart: one load honoured
     * `?variant=<closed>` and left no card checked; the next substituted the
     * first buyable size server-side and checked its card. The first is
     * detectable by the empty selection, the second only by the URL naming an
     * id the form does not carry. Handling one of them ships a notice that
     * appears about half the time. */
    var checked = cards(grid).filter(function (c) {
      return c.getAttribute('aria-checked') === 'true'; })[0];
    var m = /[?&]variant=(\d+)/.exec(window.location.search);
    var f = document.querySelector(
      'form[action*="/cart/add"] input[name="id"], form[action*="/cart/add"] select[name="id"]');
    var substituted = !!(m && f && m[1] !== String(f.value));
    if (checked && !substituted) return;

    /* A SUBSTITUTED PAGE IS ALREADY ON A GOOD SIZE. Moving again would
     * overrule Shopify's own choice for no gain. */
    if (substituted && checked) return;

    var v = currentVariant();
    var size = v && (v.options || []).filter(function (o) {
      return String(o).indexOf(' cm)') > -1; })[0];

    var m = /\((\d+)x(\d+) cm\)/.exec(size || '');
    if (!m) return;
    /* A HIDDEN SIZE THAT CAN BE BOUGHT STAYS SELECTED (repo copy, 2026-09-24):
     * the select now carries every size of the shape, so a Rug Finder link to a
     * size the ladder does not draw lands on that size, not the nearest card.
     * Only a link that NAMES the variant: a plain visit whose first variant is
     * a hidden size still moves to the nearest card, or no size shows chosen. */
    if (/[?&]variant=\d/.test(window.location.search) && v && v.available !== false) {
      var sel0 = selectFor(grid);
      if (sel0 && optionFor(sel0, '(' + m[1] + 'x' + m[2] + ' cm)')) return;
    }
    var w = Number(m[1]), l = Number(m[2]);
    /* SAME SHAPE. Falling back to whatever the select happened to list first
     * moved a customer who wanted an 80x100 RECTANGLE onto an 80x80 SQUARE. */
    var fam = w === l ? 'square' : (w <= 120 && l / w >= 2.5) ? 'runner' : 'rectangle';
    function pick(buyable) {
      return nearestInFamily(fam, w * l, buyable) || nearestInFamily('rectangle', w * l, buyable) ||
             nearestInFamily(fam, w * l) || nearestInFamily('rectangle', w * l);
    }
    function start(target) {
      if (!target) return;
      /* Wait for the picker to upgrade: this file is deferred, but until
       * `customElements` defines <variant-picker> there is nothing listening and
       * the change is swallowed. */
      var done = false;
      var go = function () { if (!done) { done = true; choose(target); } };
      if (window.customElements && customElements.whenDefined) {
        customElements.whenDefined('variant-picker').then(go);
        setTimeout(go, 2500);
      } else { go(); }
    }
    /* Stock is read once, only on this off-ladder path (rare), from the
       product's own .js; on any failure the old nearest-size rule stands. */
    var handle = (window.location.pathname.split('/products/')[1] || '').split(/[/?#]/)[0];
    if (!handle || !window.fetch) { start(pick(null)); return; }
    fetch('/products/' + handle + '.js').then(function (r) { return r.ok ? r.json() : null; }).then(function (j) {
      var ok = null;
      if (j && j.variants) { ok = {}; j.variants.forEach(function (v) { if (v.available) ok[String(v.id)] = 1; }); }
      start(pick(ok));
    }).catch(function () { start(pick(null)); });
  }

  /* THE SIZE DRAWER OPENS ONCE A SHAPE HAS BEEN CHOSEN, for the session.
   *
   * Shape decides which sizes exist, so the sizes stay closed until the first
   * question is answered. Shopify always has a variant selected, so there is
   * no server-side way to tell "the customer picked Rectangle" from "Rectangle
   * is what loaded" — the difference is an interaction, and an interaction is
   * a browser fact.
   *
   * sessionStorage, not localStorage: it should last the visit, not follow
   * somebody back next month. Wrapped, because it throws in a private window
   * and the drawer must still work when it does — one click, and the summary
   * already names the selection. */
  var CHOSE = 'rz-shape-chosen';

  function rememberShapeChoice() {
    try { sessionStorage.setItem(CHOSE, '1'); } catch (e) { /* no store, no memory */ }
  }

  function openSizeIfShapeChosen() {
    var d = document.querySelector('[data-rz-size-drawer]');
    if (!d || d.open) return;
    var chosen = false;
    try { chosen = sessionStorage.getItem(CHOSE) === '1'; } catch (e) { chosen = false; }
    if (chosen) d.open = true;
  }

  document.addEventListener('change', function (e) {
    var el = e.target;
    if (el && el.dataset && el.dataset.optionName &&
        el.dataset.optionName.toLowerCase() !== 'size') {
      rememberShapeChoice();
    }
  }, true);

  function boot() { openSizeIfShapeChosen(); reconcileOffLadder(); label(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* THE ADDRESS BAR NAMES THE RUG ON THE PAGE.
   *
   * Measured on live: choosing 5'3" x 5'3" moved the price to $425.10 and the
   * cart form to the right variant while the URL still named the one the page
   * had loaded with. The cart was right and the link was wrong, which is the
   * harder half to notice — a customer copying that link sends somebody else
   * to a different size, and every share, bookmark and paste carries it.
   *
   * `replaceState`, not `pushState`: picking sizes is browsing one product,
   * not walking a history somebody should have to press Back through. */
  function syncUrl(select) {
    if (!select) return;
    var opt = select.options[select.selectedIndex];
    var id = opt && opt.getAttribute('data-variant-id');
    if (!id) return;
    try {
      var u = new URL(window.location.href);
      if (u.searchParams.get('variant') === id) return;
      u.searchParams.set('variant', id);
      window.history.replaceState({}, '', u.toString());
    } catch (e) { /* older browser: the form is still correct */ }
  }

  function mark(grid, card) {
    cards(grid).forEach(function (c) {
      var on = c === card;
      c.setAttribute('aria-checked', String(on));
      c.tabIndex = on ? 0 : -1;
    });
    var head = (grid.closest('.rz-size') || document).querySelector('[data-rz-current]');
    if (head) {
      var n = card.querySelector('.rz-sg__nominal');
      if (n) head.textContent = n.textContent.trim();
    }
  }

  /* THE HEADER SHOWS THE RUNG WHEN A RUNG IS SELECTED, and the variant's own
   * measurement when the selection is a size no card represents. Both are the
   * truth; the first is the reference's compact form and the second is the
   * only honest thing to say about a size that was demoted to the disclosure.
   *
   * IT IS RE-APPLIED FROM A MutationObserver, NOT ONCE ON CHANGE.
   * `variant-picker.js` MORPHS the whole element after every selection and
   * re-renders the header from Liquid, where the value is the raw option
   * string — measured: the header read "8' × 10'" for about a second and then
   * reverted to `7'10" x 9'10"  (240x300 cm)` on its own. Anything written
   * once is undone by the next morph. */
  function label() {
    var grid = document.querySelector(GRID);
    var head = document.querySelector('[data-rz-current]');
    if (!grid || !head) return;
    var on = cards(grid).filter(function (c) {
      return c.getAttribute('aria-checked') === 'true';
    })[0];
    var want;
    if (on) {
      var n = on.querySelector('.rz-sg__nominal');
      want = n ? n.textContent.trim() : null;
    } else {
      var sel = selectFor(grid);
      want = sel ? (sel.value || '').replace(/\s+/g, ' ').trim() : null;
    }
    if (want && head.textContent.trim() !== want) head.textContent = want;
  }

  var picker = document.querySelector('variant-picker');
  if (picker && window.MutationObserver) {
    new MutationObserver(function () { label(); })
      .observe(picker, { childList: true, subtree: true, characterData: true });
  }

  /* ── CHANGING SHAPE NO LONGER RELOADS THE PAGE ──────────────────────────
   *
   * It used to cost a full navigation, and the reason was not the theme's:
   * Shape x Size is non-cartesian, so switching Shape alone asks for a
   * combination that is not a variant, and the other shape's variant ids are
   * not in the document at all — `product.variants` and `/products/<h>.js`
   * both cap at 250 while these products carry 353. There was nowhere to go
   * but a new page.
   *
   * The push now writes `custom.size_ladder`: the open ladder with its variant
   * ids, about 39 entries. With it, the switch is Horizon's OWN in-place path —
   * `fetchUpdatedSection` fetches the product HTML and morphs `main`, which is
   * what it already does for combined listings. The price, the cart form, the
   * grid and the drawer all come from that one morph, so nothing here has to
   * update them by hand and nothing can drift.
   *
   * IT LANDS ON THE NEAREST RUNG, NOT THE FIRST. Somebody looking at an
   * 8' x 10' rectangle who asks for Oval should arrive at the largest oval we
   * make; the map carries the centimetres so that comparison is possible.
   *
   * FALLS BACK RATHER THAN BREAKING. No map — a product the push has not
   * reached — and the shape entry still navigates, exactly as before.
   */
  function ladderMap() {
    var n = document.querySelector('script.rug-size-ladder');
    if (!n) return null;
    try {
      var d = JSON.parse(n.textContent);
      return typeof d === 'string' ? JSON.parse(d) : d;
    } catch (e) { return null; }
  }

  function currentArea() {
    var grid = document.querySelector(GRID);
    var on = grid && cards(grid).filter(function (c) {
      return c.getAttribute('aria-checked') === 'true'; })[0];
    if (!on) return null;
    return Number(on.getAttribute('data-w-cm')) * Number(on.getAttribute('data-l-cm'));
  }

  function targetFor(shape) {
    var map = ladderMap();
    var list = map && map[shape];
    if (!list || !list.length) return null;
    var want = currentArea();
    if (want == null) return list[Math.floor(list.length / 2)];
    var best = null;
    list.forEach(function (e) {
      var d = Math.abs(e.w * e.l - want);
      if (!best || d < best[0]) best = [d, e];
    });
    return best && best[1];
  }

  /* ── THE SIZES CHANGE IN A FRAME, NOT IN FIVE SECONDS ────────────────────
   * The grid holds every shape's cards; this shows one. It runs BEFORE the
   * variant update and does not wait for it, because the sizes are what the
   * customer is looking at and the price is not — measured on bbb134-aspen,
   * the first runner card used to arrive 4,975 ms after the press and the
   * grid settled at 5,610 ms.
   *
   * IT RETURNS WHETHER IT FOUND THE SHAPE, and the caller does nothing
   * different if it did not: a product whose push predates `custom.size_ladder`
   * renders one family, this finds no group for the others, and the fetch is
   * still the thing that changes the grid. Slow is not broken. */
  function showFamily(shape) {
    var fam = String(shape || '').trim().toLowerCase();
    var groups = document.querySelectorAll('[data-rz-family]');
    if (groups.length < 2) return false;
    var found = false;
    for (var i = 0; i < groups.length; i++) {
      var on = groups[i].getAttribute('data-rz-family') === fam;
      groups[i].hidden = !on;
      if (on) found = true;
    }
    if (!found) {
      /* PUT IT BACK RATHER THAN LEAVE AN EMPTY GRID. A shape with no group is
       * a shape this page cannot draw, and an empty box is worse than the old
       * one until the fetch lands. */
      var root0 = document.querySelector(GRID);
      var was = root0 && root0.getAttribute('data-shape');
      for (var j = 0; j < groups.length; j++) {
        groups[j].hidden = groups[j].getAttribute('data-rz-family') !== was;
      }
      return false;
    }
    var root = document.querySelector(GRID);
    if (root) root.setAttribute('data-shape', fam);
    return true;
  }

  document.addEventListener('change', function (e) {
    var el = e.target;
    if (!el || !el.dataset || !el.dataset.optionName) return;
    if (el.dataset.optionName.toLowerCase() === 'size') return;

    var shape = (el.value || '').trim();
    showFamily(shape);
    var entry = targetFor(shape);
    var picker = document.querySelector('variant-picker');
    if (!entry || !picker || typeof picker.fetchUpdatedSection !== 'function') {
      /* FALLBACK: no map, or a Horizon that no longer exposes the method.
       * The shape entry still gets there, with a navigation. */
      var node = document.querySelector('script.rug-shape-entry');
      if (!node) return;
      var m = {};
      try { m = JSON.parse(JSON.parse(node.textContent)); }
      catch (e2) { try { m = JSON.parse(node.textContent); } catch (e3) { return; } }
      if (m[shape]) {
        e.stopImmediatePropagation();
        window.location.search = '?variant=' + m[shape];
      }
      return;
    }

    /* Horizon would otherwise request (new shape + old size), which is not a
     * variant. Stop it and ask for the one we know exists. */
    e.stopImmediatePropagation();
    rememberShapeChoice();
    instantPrice(entry.v, el);

    var base = (picker.dataset.productUrl || window.location.pathname).split('?')[0];
    picker.fetchUpdatedSection(base + '?variant=' + entry.v,
                               { morphElementSelector: 'main' });
    try {
      var u = new URL(window.location.href);
      u.searchParams.set('variant', entry.v);
      window.history.replaceState({}, '', u.toString());
    } catch (e4) { /* older browser: the form is still correct */ }
  }, true);

  document.addEventListener('click', function (e) {
    var card = e.target.closest ? e.target.closest(CARD) : null;
    if (card) choose(card);
  });

  /* KEYBOARD — a radiogroup moves with the arrows and selects on arrival,
   * which is the platform behaviour for radios and what a screen-reader user
   * expects; Space and Enter also select, for anyone arriving by Tab. */
  document.addEventListener('keydown', function (e) {
    var card = e.target.closest ? e.target.closest(CARD) : null;
    if (!card) return;
    var grid = gridOf(card);
    if (!grid) return;
    var list = cards(grid).filter(buyable);
    if (!list.length) return;
    var i = list.indexOf(card);
    var next = null;
    switch (e.key) {
      case 'ArrowRight': case 'ArrowDown': next = list[(i + 1) % list.length]; break;
      case 'ArrowLeft': case 'ArrowUp': next = list[(i - 1 + list.length) % list.length]; break;
      case 'Home': next = list[0]; break;
      case 'End': next = list[list.length - 1]; break;
      case ' ': case 'Enter': e.preventDefault(); choose(card); return;
      default: return;
    }
    e.preventDefault();
    next.focus();
    choose(next);
  });

  /* THE SELECT REMAINS THE SOURCE OF TRUTH, so a change made anywhere else —
   * the disclosure's own select, a deep link, the Rug Finder's ?variant= — is
   * reflected back onto the cards rather than leaving two controls disagreeing. */
  document.addEventListener('change', function (e) {
    var sel = e.target;
    if (!sel || sel.tagName !== 'SELECT' || sel.name.indexOf('options[') !== 0) return;
    var scope = sel.closest('variant-picker') || document;
    var grid = scope.querySelector(GRID);
    if (!grid) return;
    var m = /\((\d+)x(\d+) cm\)/.exec(sel.value || '');
    var match = null;
    if (m) {
      match = cards(grid).filter(function (c) {
        return c.getAttribute('data-w-cm') === m[1] && c.getAttribute('data-l-cm') === m[2];
      })[0] || null;
    }
    if (match) {
      mark(grid, match);
      syncUrl(sel);
    } else {
      /* A DEMOTED SIZE IS SELECTED AND NO CARD REPRESENTS IT. Clearing every
       * card is the honest state: leaving the last one lit would claim the
       * customer had chosen a rung they did not. The header still names the
       * real selection, because it reads the select. */
      cards(grid).forEach(function (c) { c.setAttribute('aria-checked', 'false'); });
      var head = (grid.closest('.rz-size') || document).querySelector('[data-rz-current]');
      if (head) head.textContent = (sel.value || '').split('  (')[0];
      var first = cards(grid)[0];
      if (first) first.tabIndex = 0;
    }
  });
})();
